import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
// Use raw context lookup instead of useOrg() — the fallback route
// (/membership/payment-*) is registered OUTSIDE OrgLayout, so the
// provider is null. useOrg() throws in that case → blank white screen.
import { OrgContext } from '../context/OrgContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './MembershipPaymentResult.css';

/**
 * Full-page "Payment Complete" confirmation shown after Paymob redirects
 * the guest back. Three variants keyed by URL:
 *   /o/:slug/membership/payment-success  → success (green celebratory)
 *   /o/:slug/membership/payment-refunded → refunded (amber)
 *   /o/:slug/membership/payment-error    → failed (red)
 *
 * Auto-navigates to /o/:slug/membership after 8s so the guest sees their
 * activated card. All CTAs also point to the org's membership page —
 * NEVER to the platform home.
 */
const MembershipPaymentResult = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Optional org context — null when we're on the fallback route.
  const orgCtx = useContext(OrgContext);
  const orgSlug = orgCtx?.orgSlug || null;
  const orgName = orgCtx?.orgName || null;

  const variant = pathname.endsWith('/payment-refunded')
    ? 'refunded'
    : pathname.endsWith('/payment-error')
      ? 'error'
      : 'success';

  const errorMessage = searchParams.get('message');
  const orderRef = searchParams.get('order');
  const [secondsLeft, setSecondsLeft] = useState(8);

  // Where the "View my membership" CTA points AND where the auto-redirect
  // sends the guest. If we know the org → org-scoped membership page;
  // otherwise a plain /membership fallback (also handled by App.js). Never
  // the platform home — the guest just paid, they should see membership,
  // not the marketing homepage.
  const membershipHref = orgSlug ? `/o/${orgSlug}/membership` : '/membership';

  // Auto-redirect to the membership page after ~8s (successful path only —
  // errors let the guest read before going anywhere).
  useEffect(() => {
    if (variant !== 'success') return;
    const tick = setInterval(() => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    const to = setTimeout(() => navigate(membershipHref, { replace: true }), 8000);
    return () => { clearInterval(tick); clearTimeout(to); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, membershipHref]);

  return (
    <>
      <Navbar />
      <div className={`mpr-page mpr-${variant}`}>

        {/* Decorative background orbs — celebratory but on-brand */}
        <div className="mpr-orb mpr-orb-a" />
        <div className="mpr-orb mpr-orb-b" />
        <div className="mpr-orb mpr-orb-c" />

        <div className="mpr-card">

          {variant === 'success' && (
            <div className="mpr-icon-wrap mpr-icon-success" aria-hidden>
              <div className="mpr-check-ring">
                <svg viewBox="0 0 52 52" className="mpr-check-svg">
                  <circle cx="26" cy="26" r="24" className="mpr-check-circle" />
                  <path d="M14 27l8 8 16-18" className="mpr-check-tick" />
                </svg>
              </div>
              <div className="mpr-pulse mpr-pulse-1" />
              <div className="mpr-pulse mpr-pulse-2" />
            </div>
          )}
          {variant === 'refunded' && (
            <div className="mpr-icon-wrap mpr-icon-refunded" aria-hidden>
              <i className="bi bi-arrow-counterclockwise" />
            </div>
          )}
          {variant === 'error' && (
            <div className="mpr-icon-wrap mpr-icon-error" aria-hidden>
              <i className="bi bi-exclamation-triangle-fill" />
            </div>
          )}

          <div className="mpr-eyebrow">
            {variant === 'success' && `${orgName || 'Membership'} · Confirmed`}
            {variant === 'refunded' && 'Refund Processed'}
            {variant === 'error' && 'Payment Not Completed'}
          </div>

          <h1 className="mpr-title">
            {variant === 'success' && 'You’re in.'}
            {variant === 'refunded' && 'Your payment was refunded'}
            {variant === 'error' && 'Something went wrong'}
          </h1>

          <p className="mpr-lead">
            {variant === 'success' && (
              <>
                Welcome to <strong>{orgName || 'the club'}</strong>. Your
                membership is now active and a confirmation email is on
                its way with your member code and benefits.
              </>
            )}
            {variant === 'refunded' && (
              <>
                We have reversed the charge on your card. If this wasn’t
                expected, please reach out to our team and we’ll help
                sort it out.
              </>
            )}
            {variant === 'error' && (
              errorMessage || 'We could not confirm your payment. If your bank shows the charge, contact support and we’ll investigate.'
            )}
          </p>

          {orderRef && (
            <div className="mpr-order-pill">
              <i className="bi bi-hash" />
              Reference {orderRef}
            </div>
          )}

          <div className="mpr-actions">
            <a href={membershipHref} className="mpr-btn-primary">
              <i className="bi bi-person-badge" />
              View my membership
            </a>
            {variant === 'error' && (
              <a href={membershipHref} className="mpr-btn-ghost">
                <i className="bi bi-arrow-repeat" />
                Try again
              </a>
            )}
          </div>

          {variant === 'success' && (
            <div className="mpr-countdown">
              Redirecting to your membership page in <strong>{secondsLeft}</strong>s…
            </div>
          )}

          {/* Trust cues — same footer motif as the widget */}
          <div className="mpr-trust">
            <span><i className="bi bi-shield-lock-fill" /> Secured by Paymob</span>
            <span><i className="bi bi-envelope-check" /> Confirmation emailed</span>
            <span><i className="bi bi-headset" /> 24/7 support</span>
          </div>

        </div>
      </div>
      <Footer />
    </>
  );
};

export default MembershipPaymentResult;
